
import React, { useState, useEffect, useRef } from 'react';
import { Message, User } from '../types';
import { api } from '../services/api';
import { supabase } from '../services/supabase';
import { Send, User as UserIcon, Loader2, Zap } from 'lucide-react';
import { Button } from './ui';

interface ChatWidgetProps {
  orderId: string;
  user: User;
}

const QUICK_REPLIES = [
  "Hello, checking in on this case.",
  "Design is in progress.",
  "Please check the margin on #14.",
  "Case accepted, proceeding to mill.",
  "Files received, thank you.",
  "Is the shade correct?"
];

export const ChatWidget = ({ orderId, user }: ChatWidgetProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadMessages();

    // Subscribe to new messages for this order
    const channel = supabase
      .channel(`messages:${orderId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `order_id=eq.${orderId}`
      }, (payload) => {
        loadMessages();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [orderId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadMessages = async () => {
    const data = await api.getMessages(orderId);
    setMessages(data);
  };

  const handleSend = async (e?: React.FormEvent, content?: string) => {
    if (e) e.preventDefault();
    const msgToSend = content || newMessage;
    if (!msgToSend.trim()) return;

    setLoading(true);
    try {
      await api.sendMessage(orderId, user.id, msgToSend);
      setNewMessage('');
      setShowQuickReplies(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[500px] bg-background/20 border border-border rounded-xl overflow-hidden relative">
      <div className="p-4 bg-surface border-b border-border font-bold flex justify-between">
        <span className="text-primary">Case Discussion</span>
        <span className="text-xs font-normal text-secondary flex items-center gap-1">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div> Live
        </span>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center text-secondary py-10 text-sm">
            No messages yet. Start the conversation.
          </div>
        )}

        {messages.map((msg) => {
          const isMe = msg.senderId === user.id;
          return (
            <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${isMe ? 'bg-blue-600 text-white rounded-br-none' : 'bg-surfaceHighlight text-primary rounded-bl-none'}`}>
                {!isMe && <p className="text-[10px] text-secondary mb-1 font-bold">{msg.senderName}</p>}
                <p className="text-sm">{msg.content}</p>
                <p className={`text-[10px] mt-1 text-right ${isMe ? 'text-blue-200' : 'text-secondary'}`}>
                  {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {showQuickReplies && (
        <div className="absolute bottom-16 left-2 right-2 bg-zinc-800 border border-white/10 rounded-xl p-2 shadow-xl z-20 grid grid-cols-2 gap-2 animate-slide-up">
          {QUICK_REPLIES.map((reply, i) => (
            <button
              key={i}
              onClick={() => handleSend(undefined, reply)}
              className="text-left text-xs p-2 bg-black/40 hover:bg-blue-600 hover:text-white text-zinc-300 rounded transition-colors"
            >
              {reply}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={handleSend} className="p-3 bg-surface border-t border-border flex gap-2 relative z-10">
        <button
          type="button"
          onClick={() => setShowQuickReplies(!showQuickReplies)}
          className="p-2 text-secondary hover:text-yellow-400 hover:bg-surfaceHighlight rounded-lg transition-colors"
          title="Quick Replies"
        >
          <Zap size={18} />
        </button>
        <input
          className="flex-1 bg-surfaceHighlight border border-border rounded-lg px-4 py-2 text-primary focus:outline-none focus:border-secondary text-sm"
          placeholder="Type a message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
        />
        <Button type="submit" disabled={loading || !newMessage.trim()} className="px-3">
          {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        </Button>
      </form>
    </div>
  );
};
